import { useState, useCallback } from 'react';
import { useConfig } from '../../contexts/ConfigContext';
import { useLog } from '../../contexts/LogContext';
import { api } from '../../utils/api';
import type { DistributionBoardFileRecord } from './types';

export const DeleteDistributionBoardFileContainer = () => {
  const { token, projectId } = useConfig();
  const { log, clearLog } = useLog();

  const [distributionBoardFiles, setDistributionBoardFiles] = useState<
    DistributionBoardFileRecord[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchDistributionBoardFiles = useCallback(async () => {
    if (!projectId) {
      log('err', 'Project ID is required to fetch distribution board files.');
      return;
    }

    setLoading(true);
    setSelectedId(null);
    clearLog();

    try {
      const response = await api<DistributionBoardFileRecord[]>(
        'GET',
        `/api/quantity-surveyor-distribution-board-files?projectId=${projectId}`,
        undefined,
        token,
      );

      if (response.status === 200) {
        const files = response.data;
        setDistributionBoardFiles(files);
        log(
          'ok',
          `Found ${files.length} distribution board file(s) for project ${projectId}.`,
        );
      } else {
        log(
          'err',
          `Failed to fetch distribution board files (${response.status}): ${JSON.stringify(response.data)}`,
        );
      }
    } catch (error) {
      log(
        'err',
        `Error fetching distribution board files: ${(error as Error).message}`,
      );
    } finally {
      setLoading(false);
    }
  }, [projectId, token, log, clearLog]);

  const handleDelete = useCallback(async () => {
    if (selectedId === null || deleting) return;

    const selectedFile = distributionBoardFiles.find(
      (file) => file.id === selectedId,
    );
    if (!selectedFile) return;

    setDeleting(true);

    try {
      log(
        'step',
        `Deleting distribution board file #${selectedId} ("${selectedFile.name || 'unnamed'}")...`,
      );

      const response = await api(
        'DELETE',
        `/api/quantity-surveyor-distribution-board-files/${selectedId}`,
        undefined,
        token,
      );

      if (response.status === 200) {
        log(
          'ok',
          `Distribution board file #${selectedId} deleted successfully.`,
        );
        setDistributionBoardFiles((prev) =>
          prev.filter((file) => file.id !== selectedId),
        );
        setSelectedId(null);
      } else {
        log(
          'err',
          `Failed to delete (${response.status}): ${JSON.stringify(response.data)}`,
        );
      }
    } catch (error) {
      log(
        'err',
        `Error deleting distribution board file: ${(error as Error).message}`,
      );
    } finally {
      setDeleting(false);
    }
  }, [selectedId, deleting, distributionBoardFiles, token, log]);

  const selectedFile = distributionBoardFiles.find(
    (file) => file.id === selectedId,
  );

  const inputClass =
    'bg-[#0d1117] border border-[#30363d] text-[#c9d1d9] px-2.5 py-2 rounded-md text-sm font-mono focus:outline-none focus:border-[#58a6ff]';
  const btnBase =
    'px-5 py-2.5 rounded-md font-semibold text-sm text-white cursor-pointer transition-opacity disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <div>
      <div className="bg-[#161b22] border border-[#30363d] rounded-lg p-4 mb-4">
        <h2 className="text-sm text-[#8b949e] uppercase tracking-wide mb-3">
          Find Distribution Board Files
        </h2>
        <div className="flex gap-3 items-end">
          <div>
            <label className="block text-[12px] text-[#8b949e] mb-1">
              Project ID
            </label>
            <input
              type="text"
              className={inputClass}
              value={projectId}
              disabled
              style={{ width: 100 }}
            />
          </div>
          <button
            className={`${btnBase} bg-[#1f6feb] hover:opacity-85`}
            onClick={fetchDistributionBoardFiles}
            disabled={loading || !projectId}
          >
            {loading ? 'Loading...' : 'Fetch Distribution Board Files'}
          </button>
        </div>
      </div>

      {distributionBoardFiles.length > 0 && (
        <div className="bg-[#161b22] border border-[#30363d] rounded-lg p-4 mb-4">
          <h2 className="text-sm text-[#8b949e] uppercase tracking-wide mb-3">
            Distribution Board Files ({distributionBoardFiles.length})
          </h2>
          <div className="max-h-[400px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[#8b949e] text-left border-b border-[#30363d]">
                  <th className="py-2 px-3 w-[80px]">ID</th>
                  <th className="py-2 px-3">Name</th>
                  <th className="py-2 px-3 w-[180px]">Created</th>
                </tr>
              </thead>
              <tbody>
                {distributionBoardFiles.map((file) => (
                  <tr
                    key={file.id}
                    className={`border-b border-[#21262d] cursor-pointer transition-colors ${
                      selectedId === file.id
                        ? 'bg-[#1f6feb22] text-[#58a6ff]'
                        : 'hover:bg-[#21262d]'
                    }`}
                    onClick={() => setSelectedId(file.id)}
                  >
                    <td className="py-2 px-3 font-mono">{file.id}</td>
                    <td className="py-2 px-3">{file.name || '(unnamed)'}</td>
                    <td className="py-2 px-3 text-[#8b949e]">
                      {new Date(file.createdAt).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedFile && (
        <div className="bg-[#161b22] border border-[#30363d] rounded-lg p-4 mb-4">
          <h2 className="text-sm text-[#8b949e] uppercase tracking-wide mb-3">
            Selected Distribution Board File
          </h2>
          <div className="text-[#c9d1d9] mb-3">
            <span className="text-[#8b949e]">ID:</span> {selectedFile.id}
            {' · '}
            <span className="text-[#8b949e]">Name:</span>{' '}
            {selectedFile.name || '(unnamed)'}
            {' · '}
            <span className="text-[#8b949e]">Created:</span>{' '}
            {new Date(selectedFile.createdAt).toLocaleString()}
          </div>
          <button
            className={`${btnBase} bg-[#da3633] hover:opacity-85`}
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting
              ? 'Deleting...'
              : `Delete Distribution Board File #${selectedFile.id}`}
          </button>
        </div>
      )}
    </div>
  );
};
